import React, { useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LayoutDashboard,
  Receipt,
  Users,
  UserPlus,
  Settings,
  Menu as MenuIcon,
  LogOut,
  TrendingUp,
  ShieldCheck,
  Package,
  Sun,
  Moon,
  Building2,
  ShoppingCart,
  TrendingDown,
  BarChart3,
  History,
  User,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowRightLeft,
  DollarSign,
  FlaskConical,
  Wrench,
} from 'lucide-react';
import { useThemeMode } from '../contexts/ThemeContext';
import logo from '../assets/logo-perfume-stories-light.png';
import logoDark from '../assets/logo-perfume-stories-dark.png';
import Home from '../components/Home';
import Billing from '../components/Billing';
import Sales from '../components/Sales';
import Customers from '../components/Customers';
import Staff from '../components/Staff';
import BillingSettings from '../components/BillingSettings';
import ModuleManagement from '../components/ModuleManagement';
import InventoryComponent from '../components/Inventory';
import Creditors from '../components/Creditors';
import GRN from '../components/GRN';
import Expenses from '../components/Expenses';
import Reports from '../components/Reports';
import ActivityLogs from '../components/ActivityLogs';
import Returns from '../components/Returns';
import StockHistory from '../components/StockHistory';
import StockTransfer from '../components/StockTransfer';
import Suppliers from '../components/Suppliers';
import PurchaseOrders from '../components/PurchaseOrders';
import TradePayables from '../components/TradePayables';
import TradeReceivables from '../components/TradeReceivables';
import CashBankLedger from '../components/CashBankLedger';
import StockAdjustment from '../components/StockAdjustment';
import MigrationTool from '../components/MigrationTool';
import DecantTransfer from '../components/DecantTransfer';

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

// ── Section definitions ──────────────────────────────────────────────────────
// Each section has an id, label, default open state, and its menu items.
// Sections with `alwaysOpen: true` cannot be collapsed (Dashboard).
const NAV_SECTIONS = [
  {
    id: 'dashboard',
    items: [
      { path: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard', module: 'dashboard' },
    ],
    alwaysOpen: true,
  },
  {
    id: 'purchasing',
    label: 'Purchasing',
    defaultOpen: true,
    items: [
      { path: '/suppliers', icon: <Building2 size={18} />, label: 'Suppliers', module: 'suppliers' },
      { path: '/purchase-orders', icon: <ShoppingCart size={18} />, label: 'Purchase Orders', module: 'purchaseOrders' },
      { path: '/grn', icon: <Package size={18} />, label: 'GRN - Store', module: 'grn' },
      { path: '/stock-transfer', icon: <ArrowRightLeft size={18} />, label: 'Stock Transfer', module: 'stockTransfer' },
      { path: '/decant-transfer', icon: <FlaskConical size={18} />, label: 'Decant Transfer', module: 'decantTransfer' },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    defaultOpen: true,
    items: [
      { path: '/inventory', icon: <Package size={18} />, label: 'Inventory', module: 'inventory' },
      { path: '/stock-history', icon: <History size={18} />, label: 'Stock History', module: 'stockHistory' },
      { path: '/stock-adjustment', icon: <TrendingDown size={18} />, label: 'Stock Adjustment', module: 'stockAdjustment' },
    ],
  },
  {
    id: 'sales',
    label: 'Sales',
    defaultOpen: true,
    items: [
      { path: '/billing', icon: <Receipt size={18} />, label: 'Billing', module: 'billing' },
      { path: '/returns', icon: <RotateCcw size={18} />, label: 'Returns & Refunds', module: 'returns' },
      { path: '/customers', icon: <Users size={18} />, label: 'Customers', module: 'customers' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    defaultOpen: true,
    items: [
      { path: '/creditors', icon: <Building2 size={18} />, label: 'Receivables', module: 'creditors' },
      { path: '/trade-payables', icon: <TrendingDown size={18} />, label: 'Trade Payables', module: 'tradePayables' },
      { path: '/cash-bank-ledger', icon: <DollarSign size={18} />, label: 'Cash & Bank Ledger', module: 'cashBankLedger' },
      { path: '/expenses', icon: <TrendingDown size={18} />, label: 'Expenses', module: 'expenses' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    defaultOpen: true,
    items: [
      { path: '/reports', icon: <BarChart3 size={18} />, label: 'Reports', module: 'reports' },
      { path: '/analytics', icon: <TrendingUp size={18} />, label: 'Analytics', module: 'sales' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    defaultOpen: false, // collapsed by default — used infrequently
    items: [
      { path: '/staff', icon: <UserPlus size={18} />, label: 'Staff Management', module: 'staff' },
      { path: '/activity-logs', icon: <History size={18} />, label: 'Activity Logs', module: 'activityLogs' },
      { path: '/settings', icon: <Settings size={18} />, label: 'Settings', module: 'settings' },
      { path: '/modules', icon: <ShieldCheck size={18} />, label: 'Module Management', module: 'modules' },
      { path: '/admin-tools', icon: <Wrench size={18} />, label: 'Admin Tools', module: 'adminTools', adminOnly: true },
    ],
  },
];

const Dashboard = ({ user, permissions, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  // Per-section open/closed state — persisted in localStorage
  const [sectionOpen, setSectionOpen] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarSections');
      if (saved) return JSON.parse(saved);
    } catch {}
    // Default: use each section's defaultOpen value
    const defaults = {};
    NAV_SECTIONS.forEach(s => { defaults[s.id] = s.defaultOpen ?? true; });
    return defaults;
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleTheme } = useThemeMode();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleSidebarToggle = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    localStorage.setItem('sidebarOpen', JSON.stringify(newState));
  };

  const toggleSection = (id) => {
    setSectionOpen(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem('sidebarSections', JSON.stringify(next));
      return next;
    });
  };

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { handleMenuClose(); onLogout(); navigate('/login'); };

  const isAdmin = user?.role === 'admin' || permissions?.userType === 'admin';
  const hasPermission = (module) => {
    if (isAdmin) return true;
    return permissions?.[module] === true;
  };

  const currentLogo = theme.palette.mode === 'dark' ? logoDark : logo;

  // ── Shared item button styles ──
  const itemSx = (isActive) => ({
    borderRadius: 1.5,
    color: isActive ? theme.palette.primary.main : 'inherit',
    justifyContent: sidebarOpen ? 'flex-start' : 'center',
    px: sidebarOpen ? 1.5 : 1,
    py: 0.75,
    borderLeft: isActive ? '3px solid' : '3px solid transparent',
    borderLeftColor: isActive ? theme.palette.primary.main : 'transparent',
    backgroundColor: isActive ? `${theme.palette.primary.main}12` : 'transparent',
    '&:hover': {
      backgroundColor: isActive
        ? `${theme.palette.primary.main}18`
        : theme.palette.action.hover,
    },
    transition: 'all 0.15s',
  });

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box
        onClick={() => navigate('/dashboard')}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 64,
        }}
      >
        {sidebarOpen ? (
          <Box
            component="img"
            src={currentLogo}
            alt="Perfume Stories"
            sx={{ width: '100%', maxWidth: 150, height: 'auto', objectFit: 'contain' }}
          />
        ) : (
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: '1rem',
            }}
          >
            P
          </Box>
        )}
      </Box>

      {/* Nav */}
      <List sx={{ px: sidebarOpen ? 1 : 0.5, py: 1.5, flex: 1, overflowY: 'auto' }}>
        {NAV_SECTIONS.map((section, sIdx) => {
          // Filter items by permission
          const visibleItems = section.items.filter(item => {
            if (item.adminOnly) return isAdmin || permissions?.adminTools === true;
            return hasPermission(item.module);
          });
          if (visibleItems.length === 0) return null;

          const isExpanded = section.alwaysOpen || sectionOpen[section.id] !== false;
          const sectionHasActive = visibleItems.some(item => item.path === location.pathname);

          // Auto-expand if active route is in this section
          const effectivelyOpen = isExpanded || sectionHasActive;

          return (
            <Box key={section.id} sx={{ mb: 0.5 }}>
              {/* Section header — only shown when sidebar is expanded and section has a label */}
              {sidebarOpen && section.label && (
                <ListItemButton
                  onClick={() => toggleSection(section.id)}
                  sx={{
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 0.6,
                    mb: 0.25,
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: sectionHasActive
                        ? theme.palette.primary.main
                        : theme.palette.text.disabled,
                    }}
                  />
                  <Box sx={{ color: theme.palette.text.disabled, display: 'flex' }}>
                    {effectivelyOpen
                      ? <ChevronUp size={14} />
                      : <ChevronDown size={14} />}
                  </Box>
                </ListItemButton>
              )}

              {/* Items */}
              <Collapse in={effectivelyOpen || !sidebarOpen} timeout={200}>
                {visibleItems.map(item => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Tooltip
                      key={item.path}
                      title={!sidebarOpen ? item.label : ''}
                      placement="right"
                    >
                      <ListItem disablePadding sx={{ mb: 0.25 }}>
                        <ListItemButton
                          onClick={() => {
                            navigate(item.path);
                            if (isMobile) setMobileOpen(false);
                          }}
                          sx={itemSx(isActive)}
                        >
                          <ListItemIcon
                            sx={{
                              color: isActive
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                              minWidth: sidebarOpen ? 34 : 'auto',
                              justifyContent: 'center',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{
                                fontWeight: isActive ? 600 : 400,
                                fontSize: '0.875rem',
                              }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  );
                })}
              </Collapse>

              {/* Divider between sections (not after last) */}
              {sIdx < NAV_SECTIONS.length - 1 && sidebarOpen && (
                <Divider sx={{ mt: 0.75, mb: 0.25, opacity: 0.5 }} />
              )}
            </Box>
          );
        })}
      </List>

      {/* Developer credit */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {sidebarOpen ? (
          <>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', lineHeight: 1.4 }}>
              Developed by
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.4 }}>
              IPYNB Solutions
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', lineHeight: 1.4 }}>
              +94 72 421 8840
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', lineHeight: 1.4 }}>
              Perfume Stories v.1.0.2
            </Typography>
          </>
        ) : (
          <Tooltip title="Developed by IPYNB Solutions · +94 72 421 8840 · Perfume Stories v.1.0.2" placement="right">
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', fontWeight: 700 }}>
              IP
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : collapsedDrawerWidth}px)` },
          ml: { md: `${sidebarOpen ? drawerWidth : collapsedDrawerWidth}px` },
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: 1,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Tooltip title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
            <IconButton
              color="inherit"
              onClick={handleSidebarToggle}
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              {sidebarOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </IconButton>
          </Tooltip>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          </Typography>

          <Tooltip title="Toggle theme">
            <IconButton 
              onClick={toggleTheme} 
              color="inherit" 
              sx={{ 
                mr: 1.5,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            >
              {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Account">
            <IconButton 
              onClick={handleMenuOpen} 
              sx={{ 
                p: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                }
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: `1.5px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <User size={18} />
              </Box>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
              }
            }}
          >
            <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {user?.email || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isAdmin ? 'Administrator' : 'Staff Member'}
                </Typography>
              </Box>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.lighter',
                }
              }}
            >
              <ListItemIcon>
                <LogOut size={18} color="currentColor" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: sidebarOpen ? drawerWidth : collapsedDrawerWidth }, flexShrink: { md: 0 }, transition: 'all 0.3s ease' }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: sidebarOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : collapsedDrawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar />
        <Routes>
          <Route path="/dashboard" element={<ErrorBoundary><Home permissions={permissions} /></ErrorBoundary>} />
          <Route path="/suppliers" element={<ErrorBoundary><Suppliers currentUser={user} /></ErrorBoundary>} />
          <Route path="/purchase-orders" element={<ErrorBoundary><PurchaseOrders currentUser={user} /></ErrorBoundary>} />
          <Route path="/grn" element={<ErrorBoundary><GRN currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/stock-transfer" element={<ErrorBoundary><StockTransfer currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/decant-transfer" element={<ErrorBoundary><DecantTransfer currentUser={user} /></ErrorBoundary>} />
          <Route path="/inventory" element={<ErrorBoundary><InventoryComponent currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/stock-history" element={<ErrorBoundary><StockHistory /></ErrorBoundary>} />
          <Route path="/stock-adjustment" element={<ErrorBoundary><StockAdjustment currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/billing" element={<ErrorBoundary><Billing currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/returns" element={<ErrorBoundary><Returns currentUser={user} /></ErrorBoundary>} />
          <Route path="/creditors" element={<ErrorBoundary><Creditors currentUser={user} /></ErrorBoundary>} />
          <Route path="/cash-bank-ledger" element={<ErrorBoundary><CashBankLedger currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/trade-payables" element={<ErrorBoundary><TradePayables currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/trade-receivables" element={<ErrorBoundary><TradeReceivables currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/expenses" element={<ErrorBoundary><Expenses currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/reports" element={<ErrorBoundary><Reports permissions={permissions} /></ErrorBoundary>} />
          <Route path="/analytics" element={<ErrorBoundary><Sales permissions={permissions} /></ErrorBoundary>} />
          <Route path="/customers" element={<ErrorBoundary><Customers currentUser={user} permissions={permissions} /></ErrorBoundary>} />
          <Route path="/staff" element={<ErrorBoundary><Staff currentUser={user} /></ErrorBoundary>} />
          <Route path="/activity-logs" element={<ErrorBoundary><ActivityLogs /></ErrorBoundary>} />
          <Route path="/settings" element={<ErrorBoundary><BillingSettings /></ErrorBoundary>} />
          <Route path="/modules" element={<ErrorBoundary><ModuleManagement currentUser={user} /></ErrorBoundary>} />
          {(isAdmin || permissions?.adminTools === true) && (
            <Route path="/admin-tools" element={<ErrorBoundary><MigrationTool currentUser={user} /></ErrorBoundary>} />
          )}
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard;
